import { useQuery } from '@tanstack/react-query';
import { ALERT_CATEGORIES, ALERT_CATEGORY_LABEL, can } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconSettings } from '../icons';
import { useSession } from '../session';
import { MONITORS_EXPLAINER, alertRules, morningEmailLine } from './monitorsView';
import { MonitorSettingsRow } from './alerts/MonitorSettingsRow';
import { MutedList } from './alerts/MutedList';
import { TestingReview } from './alerts/TestingReview';
import type { MonitorSettingsRowData } from './alerts/types';

/**
 * Settings → Alerts (ops insights Phase 7 spec §7), grown from 6b's "Monitors — testing" card.
 * The owner's, top to bottom:
 *
 *  - each monitor the engine runs, grouped by its board column: On, severity, threshold,
 *    Testing / Live (asked first), what is firing now, and whether it is being checked;
 *  - the fixed rules, as text (schedule, escalation, snooze and mute choices, the latch), and the
 *    morning email's status (a yes or no from the server, never the key);
 *  - the testing review: what each monitor in Testing raised in the last 7 days, judged
 *    Useful / Not useful, "N of M judged useful", Switch to Live;
 *  - every standing mute, with Unmute (the owner lifts anyone's).
 *
 * Owner only: the reads are `alert:review` (they show Testing alerts) and `alert:configure`
 * (the mutes), the writes `alert:configure` and `alert:review`. This card does not mount for
 * anyone else, and asks for nothing first (LESSONS 2026-10-07: the gate is INSIDE the exported
 * function, the read `enabled` by it). Every read answers through the settings family's
 * contract: offline or failed never renders as "no alerts", "nothing raised" or "nothing muted".
 *
 * The anchor stays `#monitors`: the board's "Alert settings" and any pasted link land here.
 */
export function MonitorsCard({ id }: { id: string }) {
  const { role } = useSession();
  const mayRead = can(role, 'alert', 'review');
  const mayConfigure = can(role, 'alert', 'configure');
  const trpc = useTRPC();
  // Not even asked for a role the server would refuse: the card is simply not theirs.
  const q = useQuery({ ...trpc.monitor.health.queryOptions(), enabled: mayRead });
  if (!mayRead) return null;
  const state = shownState(q, q.data);
  const now = new Date();
  return (
    <SectionCard id={id} title="Alerts">
      <p className="text-sm text-muted-foreground">{MONITORS_EXPLAINER}</p>
      {!q.data ? (
        <div className="mt-3">
          <UnresolvedState
            state={unresolved(state)}
            what="the monitors"
            icon={IconSettings}
            error={q.error}
            onRetry={() => void q.refetch()}
          />
        </div>
      ) : (
        <div className="mt-3">
          <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
          <Monitors rows={q.data.monitors} tz={q.data.timeZone} now={now} />
          <section aria-labelledby="alerts-rules" className="mt-5" data-rules>
            <h3 id="alerts-rules" className="text-sm font-semibold">
              How alerts work
            </h3>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {alertRules().map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
            <p className="mt-2 text-sm" data-morning-email>
              <span className="font-medium">Morning email: </span>
              {morningEmailLine(q.data.emailConnected)}
            </p>
          </section>
          <TestingReview rows={q.data.monitors} now={now} />
        </div>
      )}
      {mayConfigure ? <MutedList now={now} /> : null}
    </SectionCard>
  );
}

/** The monitors under their board column's name, in the board's order; an empty column is left out. */
function Monitors({
  rows,
  tz,
  now,
}: {
  rows: MonitorSettingsRowData[];
  tz: string;
  now: Date;
}) {
  return (
    <div className="space-y-4">
      {ALERT_CATEGORIES.map((category) => {
        const inColumn = rows.filter((r) => r.category === category);
        if (inColumn.length === 0) return null;
        const headingId = `alerts-group-${category}`;
        return (
          <section key={category} aria-labelledby={headingId} data-category={category}>
            <h3
              id={headingId}
              className="mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              {ALERT_CATEGORY_LABEL[category]}
            </h3>
            <ul>
              {inColumn.map((row) => (
                <MonitorSettingsRow key={row.key} row={row} tz={tz} now={now} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
